import { IconChevronRight } from "@tabler/icons-react";
import clsx from "clsx";
import { useExtracted } from "#i18n";
import Link from "../Link/Link";
import Typography from "../Typography/Typography";
import styles from "./Breadcrumbs.module.scss";

export interface BreadcrumbsProps {
  children: React.ReactNode;
  className?: string;
}

const Breadcrumbs = (props: BreadcrumbsProps) => {
  const translateMessage = useExtracted();
  const { children, className } = props;

  return (
    <nav
      className={clsx(styles.breadcrumbs, className)}
      aria-label={translateMessage("Breadcrumb")}
    >
      {children}
    </nav>
  );
};

export interface BreadcrumbsListProps {
  children: React.ReactNode;
  /**
   * Keeps the trail on one line: crumbs shrink and truncate, and what still doesn't fit is clipped,
   * leaving room for the focus rings of controls in the crumbs.
   */
  nowrap?: boolean;
  className?: string;
}

const BreadcrumbsList = (props: BreadcrumbsListProps) => {
  const { children, nowrap, className } = props;

  return <ol className={clsx(styles.list, nowrap && styles.nowrap, className)}>{children}</ol>;
};

export interface BreadcrumbsBreadcrumbProps {
  href?: string;
  children: React.ReactNode;
  isCurrentPage?: boolean;
  /** Leaves the crumb out below a width (`md` or `xl`), where the trail has no room for it. */
  hiddenBelow?: "md" | "xl";
  /** Leaves out the separator after the crumb below a width, where the next crumb is left out. */
  separatorHiddenBelow?: "md" | "xl";
  className?: string;
}

const BreadcrumbsBreadcrumb = (props: BreadcrumbsBreadcrumbProps) => {
  const { href, children, isCurrentPage, hiddenBelow, separatorHiddenBelow, className } = props;

  return (
    <li
      className={clsx(
        styles.item,
        hiddenBelow === "md" && styles.hiddenBelowMd,
        hiddenBelow === "xl" && styles.hiddenBelowXl,
        className,
      )}
    >
      {href && !isCurrentPage ? (
        <Link href={href} variant="bare" className={styles.link}>
          {children}
        </Link>
      ) : (
        <Typography
          as="span"
          variant="bodySmall"
          weight={isCurrentPage ? "semibold" : "regular"}
          className={styles.current}
          data-active={isCurrentPage}
          aria-current={isCurrentPage ? "page" : undefined}
        >
          {children}
        </Typography>
      )}
      {!isCurrentPage && (
        <IconChevronRight
          size={16}
          className={clsx(
            styles.separator,
            separatorHiddenBelow === "md" && styles.hiddenBelowMd,
            separatorHiddenBelow === "xl" && styles.hiddenBelowXl,
          )}
          aria-hidden
        />
      )}
    </li>
  );
};

export interface BreadcrumbsBackButtonProps {
  children?: React.ReactNode;
  onClick?: () => void;
  href?: string;
  className?: string;
}

const BreadcrumbsBackButton = (props: BreadcrumbsBackButtonProps) => {
  const translateMessage = useExtracted();
  const { children, onClick, href, className } = props;

  const content = children || translateMessage("Back");

  if (href) {
    return (
      <Link href={href} variant="bare" className={clsx(styles.backButton, className)}>
        {content}
      </Link>
    );
  }

  return (
    <button type="button" onClick={onClick} className={clsx(styles.backButton, className)}>
      {content}
    </button>
  );
};

Breadcrumbs.List = BreadcrumbsList;
Breadcrumbs.Breadcrumb = BreadcrumbsBreadcrumb;
Breadcrumbs.BackButton = BreadcrumbsBackButton;

export default Breadcrumbs;
